import { BadgeCheck, Mail, MapPin } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { ReactElement } from 'react';

import { ProfessionalSubtypeBadge } from '@/components/professionals/ProfessionalSubtypeBadge';
import { Badge } from '@/components/ui/badge';
import type { ProfessionalRead } from '@/types/professionals-types';

interface ProfileBadgeCardProps {
  professional: ProfessionalRead;
  /** SAK e-mail the magic-link will be sent to, shown in full (unmasked) so
      the claimant can confirm the destination before clicking. SAK-registry
      addresses are already public, so this discloses nothing private. */
  email?: string | null;
}

/**
 * Identity-confirmation card. Intentionally dense so the professional can
 * verify at a glance this is really their entry before kicking off the
 * verification e-mail: avatar, name+title, city, SAK badge, e-mail.
 *
 * Kept to plain data rows (no CTAs) — actions belong in ClaimMethodsBlock.
 */
export function ProfileBadgeCard({
  professional,
  email,
}: ProfileBadgeCardProps): ReactElement {
  const t = useTranslations('Lawyers.claim');

  const initials = professional.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className='rounded-xl border border-line-default bg-surface-muted/50 p-5'>
      <div className='flex items-center gap-4'>
        <div className='flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-tertiary/30 to-status-info/30 ring-2 ring-brand-tertiary/40 text-base font-semibold text-brand-tertiary'>
          {initials}
        </div>
        <div className='min-w-0 flex-1'>
          {/* #4541: name already embeds the academic title; subtype
              surfaces via badge for cross-jurisdictional lawyers. */}
          <h3 className='font-semibold text-content-primary truncate'>
            {professional.name}
          </h3>
          <ProfessionalSubtypeBadge
            professional={professional}
            className='mt-1'
          />
          {professional.city ? (
            <div className='mt-0.5 flex items-center gap-1 text-sm text-content-secondary'>
              <MapPin className='h-3.5 w-3.5 shrink-0' />
              <span className='truncate'>{professional.city}</span>
            </div>
          ) : null}
          <div className='mt-2 flex flex-wrap items-center gap-2'>
            {professional.registry_source === 'sak' ? (
              <Badge
                variant='secondary'
                className='bg-status-info/10 text-status-info border-status-info/20 hover:bg-status-info/15 font-medium'
              >
                <BadgeCheck className='w-3 h-3 mr-1' />
                SAK
              </Badge>
            ) : null}
            {professional.registry_number ? (
              <span className='text-xs text-content-muted'>
                Reg. č. {professional.registry_number}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {email ? (
        <div className='mt-4 flex items-center gap-2 rounded-lg bg-surface-subtle px-3 py-2 text-sm'>
          <Mail className='h-4 w-4 shrink-0 text-brand-tertiary' />
          <span className='text-content-secondary'>{t('emailLabel')}:</span>
          <span className='font-mono text-content-primary'>{email}</span>
        </div>
      ) : null}
    </div>
  );
}
